"use client";

import type React from "react";
import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import type { ListingData, PostData } from "@/types/creation";
import { useShareActions, ShareContent } from "@/components/share/share-content";

interface UniversalSuccessStepProps {
  data: ListingData | PostData;
  onResetAction: () => void;
  onContinueBrowsingAction: () => void;
  type: "listing" | "post";
  sellerUsername?: string;
  userId?: string;
}

function isPostData(data: ListingData | PostData): data is PostData {
  return "content" in data;
}

function isListingData(data: ListingData | PostData): data is ListingData {
  return "price" in data || "condition" in data || "grade" in data;
}

export function UniversalSuccessStep({
  data,
  onResetAction,
  onContinueBrowsingAction,
  type,
  sellerUsername: sellerUsernameProp,
  userId,
}: UniversalSuccessStepProps): React.ReactElement {
  const [photoUrls, setPhotoUrls] = useState<string[]>([]);
  const [fetchedUsername, setFetchedUsername] = useState<string | null>(null);

  useEffect(() => {
    if (type !== "listing" || !userId || sellerUsernameProp != null) return
    let cancelled = false
    import("@/utils/supabase/client").then(({ createClient }) => {
      const supabase = createClient()
      supabase
        .from("profiles")
        .select("username")
        .eq("id", userId)
        .maybeSingle()
        .then(({ data }) => {
          if (!cancelled && data?.username) setFetchedUsername(data.username)
        })
    })
    return () => { cancelled = true }
  }, [type, userId, sellerUsernameProp])

  const sellerUsername = sellerUsernameProp ?? fetchedUsername ?? undefined

  useEffect(() => {
    const extractedUrls: string[] = [];

    if (data.photos && Array.isArray(data.photos)) {
      data.photos.forEach((photo: any) => {
        if (typeof photo === "string") {
          // If it's already a string URL
          extractedUrls.push(photo);
        } else if (photo && typeof photo === "object") {
          // If it's a PhotoItem object with preview
          if ("preview" in photo && photo.preview) {
            extractedUrls.push(photo.preview);
          } else if ("file" in photo && "preview" in photo) {
            extractedUrls.push(photo.preview);
          }
        }
      });
    }

    setPhotoUrls(extractedUrls);
  }, [data, data.photos]);

  const getTitle = () => {
    if ("title" in data && data.title) return data.title;
    if (isPostData(data)) return data.content.slice(0, 30) + (data.content.length > 30 ? "…" : "");
    return type === "listing" ? "New Listing" : "New Post";
  };

  const getDescription = () => {
    if (isListingData(data) && "description" in data && data.description) return String(data.description);
    if (isPostData(data)) return String(data.content);
    return "";
  };

  const listingId = (data as any)?.id;
  const shareUrl = listingId
    ? `/${type === "listing" ? "listings" : "posts"}/${listingId}`
    : `/${type === "listing" ? "listings" : "feed"}`;

  const priceNum =
    isListingData(data) && "price" in data && data.price != null
      ? typeof data.price === "number"
        ? data.price
        : parseFloat(String(data.price))
      : undefined;

  const currency =
    type === "listing" && isListingData(data) && "currency" in data
      ? (data as { currency?: string }).currency
      : undefined;

  const shareActions = useShareActions({
    url: shareUrl,
    title: getTitle(),
    image: photoUrls[0],
    price: priceNum,
    currency,
    variant: "item",
    storyData:
      type === "listing" && isListingData(data)
        ? { seller: sellerUsername ?? (data as any)?.seller_username ?? "" }
        : undefined,
  });

  const {
    currentUrl,
    copied,
    cardPreviewDataUrl,
    instagramLoading,
    handleCopy,
    handleWhatsApp,
    handleMessenger,
    handleTwitter,
    handleTelegram,
    handleInstagram,
    handleTikTok,
    handleEmailDefault,
    handleNativeShare,
  } = shareActions

  return (
    <div className="px-2 pt-6 pb-4">
      <p className="text-center text-lg font-semibold text-foreground mb-6">
        {type === "listing" ? "Your item has been listed! 🎉" : "Your post has been published!"}
      </p>
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2 }}
        className="w-full"
      >
        <ShareContent
          currentUrl={currentUrl}
          copied={copied}
          title={getTitle()}
          image={photoUrls[0]}
          price={priceNum}
          currency={currency}
          variant="item"
          cardPreviewDataUrl={cardPreviewDataUrl}
          instagramLoading={instagramLoading}
          onCopy={handleCopy}
          onWhatsApp={handleWhatsApp}
          onMessenger={handleMessenger}
          onTwitter={handleTwitter}
          onTelegram={handleTelegram}
          onInstagram={handleInstagram}
          onTikTok={handleTikTok}
          onEmailClick={handleEmailDefault}
          onMore={handleNativeShare}
        />
      </motion.div>
    </div>
  );
}
